import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Mail, Lock, User, Phone, Eye, EyeOff, ArrowRight, GraduationCap, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { BrandLogo } from '../components/BrandLogo';

export default function Register() {
    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        mobile: '',
        password: '',
        confirmPassword: '',
        role: 'student'
    });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (formData.password !== formData.confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        if (formData.password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }

        setLoading(true);
        
        try {
            const user = await register({
                full_name: formData.full_name,
                email: formData.email,
                mobile: formData.mobile || null,
                password: formData.password,
                role: formData.role
            });
            toast.success(`Welcome to MaceSuite, ${user.full_name}!`);
            navigate('/dashboard');
        } catch (error) {
            const message = error.response?.data?.detail || 'Registration failed. Please try again.';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex" data-testid="register-page">
            {/* Left side - Image */}
            <div className="hidden lg:flex lg:flex-1 relative bg-[#0F2A44]">
                <div 
                    className="absolute inset-0 bg-cover bg-center opacity-30"
                    style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1704748082614-8163a88e56b8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHx1bml2ZXJzaXR5JTIwc3R1ZGVudCUyMHN0dWR5aW5nJTIwbGFwdG9wfGVufDB8fHx8MTc3MDEwMDg1M3ww&ixlib=rb-4.1.0&q=85)' }}
                />
                <div className="relative z-10 flex flex-col justify-end p-12 text-white">
                    <div className="max-w-lg">
                        <h3 className="text-3xl font-bold font-['Manrope'] mb-4">
                            Join Our Learning Community
                        </h3>
                        <p className="text-lg text-white/80 leading-relaxed">
                            Whether you're an instructor shaping the next generation of accountants, 
                            or a student building your professional skills, MaceSuite provides the 
                            tools you need to succeed.
                        </p>
                    </div>
                </div>
            </div>

            {/* Right side - Form */}
            <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:px-12 xl:px-24">
                <div className="w-full max-w-md mx-auto">
                    {/* Logo */}
                    <BrandLogo
                        className="auth-brand mb-12"
                        imgClassName="auth-brand-wordmark"
                        showSubtitle
                        subtitleClassName="auth-brand-subtitle"
                    />

                    {/* Welcome text */}
                    <div className="mb-8">
                        <h2 className="text-3xl font-bold font-['Manrope'] text-slate-900 mb-2">
                            Create your account
                        </h2>
                        <p className="text-slate-600">
                            Get started with your accounting journey
                        </p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Role selection */}
                        <div>
                            <Label className="text-slate-700 mb-3 block">I am a</Label>
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, role: 'student' })}
                                    className={`p-4 rounded-xl border-2 flex flex-col items-center gap-2 transition-colors ${
                                        formData.role === 'student'
                                            ? 'border-[#2F8FCB] bg-[#E0F2FE]'
                                            : 'border-slate-200 hover:border-slate-300'
                                    }`}
                                    data-testid="role-student-btn"
                                >
                                    <GraduationCap className={`w-6 h-6 ${formData.role === 'student' ? 'text-[#2F8FCB]' : 'text-slate-400'}`} />
                                    <span className={`font-medium ${formData.role === 'student' ? 'text-[#0F2A44]' : 'text-slate-600'}`}>
                                        Student
                                    </span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, role: 'instructor' })}
                                    className={`p-4 rounded-xl border-2 flex flex-col items-center gap-2 transition-colors ${
                                        formData.role === 'instructor'
                                            ? 'border-[#2F8FCB] bg-[#E0F2FE]'
                                            : 'border-slate-200 hover:border-slate-300'
                                    }`}
                                    data-testid="role-instructor-btn"
                                >
                                    <BookOpen className={`w-6 h-6 ${formData.role === 'instructor' ? 'text-[#2F8FCB]' : 'text-slate-400'}`} />
                                    <span className={`font-medium ${formData.role === 'instructor' ? 'text-[#0F2A44]' : 'text-slate-600'}`}>
                                        Instructor
                                    </span>
                                </button>
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="full_name" className="text-slate-700">Full name</Label>
                            <div className="relative mt-2">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                <Input
                                    id="full_name"
                                    name="full_name"
                                    type="text"
                                    value={formData.full_name}
                                    onChange={handleChange}
                                    placeholder="Juan Dela Cruz"
                                    className="pl-10 h-12"
                                    required
                                    data-testid="register-name-input"
                                />
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="email" className="text-slate-700">Email address</Label>
                            <div className="relative mt-2">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                <Input
                                    id="email"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="you@university.edu"
                                    className="pl-10 h-12"
                                    required
                                    data-testid="register-email-input"
                                />
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="mobile" className="text-slate-700">Mobile number (optional)</Label>
                            <div className="relative mt-2">
                                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                <Input
                                    id="mobile"
                                    name="mobile"
                                    type="tel"
                                    value={formData.mobile}
                                    onChange={handleChange}
                                    placeholder="+63 9XX XXX XXXX"
                                    className="pl-10 h-12"
                                    data-testid="register-mobile-input"
                                />
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="password" className="text-slate-700">Password</Label>
                            <div className="relative mt-2">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                <Input
                                    id="password"
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={formData.password}
                                    onChange={handleChange}
                                    placeholder="Create a strong password"
                                    className="pl-10 pr-10 h-12"
                                    required
                                    data-testid="register-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="confirmPassword" className="text-slate-700">Confirm password</Label>
                            <div className="relative mt-2">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                <Input
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    type={showPassword ? 'text' : 'password'}
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    placeholder="Confirm your password"
                                    className="pl-10 h-12"
                                    required
                                    data-testid="register-confirm-password-input"
                                />
                            </div>
                        </div>

                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full h-12 bg-[#0F2A44] hover:bg-[#1E3A8A] text-white rounded-full font-medium btn-transition"
                            data-testid="register-submit-btn"
                        >
                            {loading ? (
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <>
                                    Create account
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </>
                            )}
                        </Button>
                    </form>

                    {/* Login link */}
                    <p className="mt-8 text-center text-slate-600">
                        Already have an account?{' '}
                        <Link
                            to="/login"
                            className="font-medium text-[#2F8FCB] hover:text-[#0F2A44]"
                            data-testid="login-link"
                        >
                            Sign in
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
